import { BreadcrumbBadgeComponent } from '../../components';
import { ConfigBtnComponent } from '../../components/config-btn/config-btn.component';
import { ConfigBtnData } from './config-btn-data';
import { RevokeTrustBtnData } from './custom-message-listeners/revoke-trust-btn-data';
import {
  ApplicationRef,
  ComponentRef,
  EmbeddedViewRef,
  EnvironmentInjector,
  Injectable,
  createComponent,
} from '@angular/core';
import {
  BreadcrumbBadge,
  BreadcrumbBadgeColorSchema,
  HeaderBarConfig,
  HeaderBarConfigService,
  LuigiCoreService,
  LuigiNode,
  LuigiNodeCategory,
  NodeItem,
} from '@openmfp/portal-ui-lib';
import '@ui5/webcomponents/dist/Breadcrumbs.js';
import { RevokeExtensionTrustButtonComponent } from 'src/app/components/revoke-extension-trust-button/revoke-extension-trust-button.component';
import { JukeboxLuigiNode } from 'src/app/models/luigi';

interface LuigiNodeExtended extends JukeboxLuigiNode {
  hasConfig: 'true' | 'false';
  extClass: 'string';
  scope: 'string';
  installableIn: 'string';
}

@Injectable({
  providedIn: 'root',
})
export class DxpHeaderBarConfigService implements HeaderBarConfigService {
  private configBtnComponentComponentRef!: ComponentRef<ConfigBtnComponent>;
  private configRevokeTrustComponentRef!: ComponentRef<RevokeExtensionTrustButtonComponent>;

  constructor(
    private luigiCoreService: LuigiCoreService,
    private environmentInjector: EnvironmentInjector,
    private appRef: ApplicationRef,
  ) {}

  async getConfig(): Promise<HeaderBarConfig> {
    const headerTitle = this.luigiCoreService.config.settings?.header.title;
    return {
      autoHide: true,
      omitRoot: false,
      clearBeforeRender: true,
      pendingItemLabel: '...',
      leftRenderers: [
        (containerElement: HTMLElement, nodeItems: NodeItem[]) => {
          const configBtnData = this.getConfigBtnData(nodeItems);
          const projectTrustData = this.getConfigRevokeTrust(nodeItems);
          this.createRevokeExtensionTrustBtn(
            containerElement,
            projectTrustData,
          );
          this.createConfigBtn(containerElement, configBtnData);

          containerElement.classList.add('dxp-breadcrumb');

          const ui5breadcrumbs: HTMLElement =
            containerElement.querySelector('ui5-breadcrumbs') ||
            document.createElement('ui5-breadcrumbs');
          ui5breadcrumbs.innerHTML = '';
          nodeItems.forEach((item, index) => {
            if (item.node?.hideFromBreadcrumb) {
              return;
            }

            const breadcrumbLabel: string = this.getBreadcrumbLabel(
              item.label ?? '',
            );

            // TODO: omit virtual tree children until there is a solution
            if (
              breadcrumbLabel &&
              !breadcrumbLabel.startsWith(':virtualSegment_')
            ) {
              const itemCmp = document.createElement('ui5-breadcrumbs-item');
              itemCmp.innerText = breadcrumbLabel;
              (itemCmp as any)._item = item;
              // Only set href/data-route for non-last items
              if (index !== nodeItems.length - 1) {
                const breadcrumbRoute = this.getBreadcrumbRoute(
                  item,
                  breadcrumbLabel,
                );
                itemCmp.setAttribute('href', breadcrumbRoute);
              }
              ui5breadcrumbs.appendChild(itemCmp);
            }
          });

          ui5breadcrumbs.addEventListener('item-click', (event: any) => {
            if (
              !(
                event.detail.ctrlKey ||
                event.detail.altKey ||
                event.detail.shiftKey ||
                event.detail.metaKey
              )
            ) {
              event.preventDefault();
              const route = event.detail.item.getAttribute('href');
              const sanitizedUrl = this.getNodeRouteWithQueryParameters(route);
              if (sanitizedUrl) {
                window.location.href = sanitizedUrl;
              }
            }
          });

          const emptyBreadcrumbSpacer: HTMLElement =
            document.getElementById('breadcrumbSpacer') ||
            document.createElement('div');
          emptyBreadcrumbSpacer.setAttribute('id', 'breadcrumbSpacer');

          if (
            nodeItems.find(
              (node: NodeItem) =>
                (node.node as JukeboxLuigiNode).hideBreadcrumbPath,
            )
          ) {
            ui5breadcrumbs.remove();
            containerElement.appendChild(emptyBreadcrumbSpacer);
          } else {
            emptyBreadcrumbSpacer.remove();
            containerElement.appendChild(ui5breadcrumbs);
          }

          const communityBadgeName = 'dxp-community-badge';
          const communityBadgeExists =
            containerElement.getElementsByClassName(communityBadgeName).length >
            0;
          if (this.shouldAddBreadcrumbBadge(nodeItems)) {
            const breadcrumbBadgeData = this.getBreadcrumbBadge(nodeItems);
            this.createBadge(
              communityBadgeName,
              breadcrumbBadgeData,
              containerElement,
              communityBadgeExists,
            );
            this.showBadgesOf(communityBadgeName);
          } else {
            this.hideBadgesOf(communityBadgeName);
          }

          const hasProjectLevelExtension = nodeItems.some(
            (nodeItem: NodeItem) =>
              (nodeItem?.node as JukeboxLuigiNode).isProjectLevelExtension,
          );
          const projectLevelBadgeName = 'dxp-project-extension-badge';

          if (hasProjectLevelExtension) {
            const projectLevelHint =
              'This is a custom extension used in this Workspace, not verified by the Hyperspace Portal.';

            const projectLevelWarning = {
              text: 'Custom Extension',
              colorSchema: '1' as BreadcrumbBadgeColorSchema,
              hint: projectLevelHint,
              icon: 'warning',
            };
            const badgeAlreadyExists =
              containerElement.getElementsByClassName(projectLevelBadgeName)
                .length > 0;

            this.createBadge(
              projectLevelBadgeName,
              projectLevelWarning,
              containerElement,
              badgeAlreadyExists,
            );
            this.showBadgesOf(projectLevelBadgeName);
          } else {
            this.hideBadgesOf(projectLevelBadgeName);
          }

          // TODO: stop abusing breadcrumb to set document title, see https://github.tools.sap/dxp/jukebox/issues/845
          if (
            nodeItems?.length > 0 &&
            nodeItems[0].node?.context?._tmpUseBreadcrumbsForTitle
          ) {
            let deepestEntity: NodeItem | undefined;
            let deepestDynNode;
            let deepestDynNodeParent: NodeItem | undefined;
            let lastItem: NodeItem | undefined;
            nodeItems.forEach((item, index) => {
              if (item.node?.defineEntity) {
                deepestEntity = item;
              }
              if (item.node?.pathSegment?.startsWith(':')) {
                deepestDynNode = item;
                deepestDynNodeParent = nodeItems[index - 1];
              }
              lastItem = item;
            });
            if (deepestEntity || deepestDynNode) {
              const entityDescribingLabel =
                deepestEntity?.node?.defineEntity?.label;
              const entityLabel = deepestEntity?.label;

              let nodeLabel = lastItem?.label;
              if (deepestDynNode === lastItem && deepestDynNodeParent?.label) {
                nodeLabel += ` (${deepestDynNodeParent.label})`;
              }

              let title = `${nodeLabel} · ${entityLabel}`;
              if (entityDescribingLabel) {
                title += ` (${entityDescribingLabel})`;
              }
              title += ` | ${headerTitle}`;
              this.luigiCoreService.ux().setDocumentTitle(title);
            }
          }

          return ui5breadcrumbs;
        },
      ],
      rightRenderers: [],
    };
  }

  hideBadgesOf(cls: string): void {
    const badgeElements = document.getElementsByClassName(cls);
    [].forEach.call(badgeElements, function (elem) {
      (elem as HTMLElement).style.display = 'none';
    });
  }

  showBadgesOf(cls: string): void {
    const badgeElements = document.getElementsByClassName(cls);
    [].forEach.call(badgeElements, function (elem) {
      (elem as HTMLElement).style.display = 'block';
    });
  }

  shouldAddBreadcrumbBadge(nodeItems: NodeItem[]): boolean {
    return nodeItems.some((nodeItem) => nodeItem?.node?.breadcrumbBadge);
  }

  getBreadcrumbBadge(nodeItems: NodeItem[]): BreadcrumbBadge | undefined {
    return nodeItems.find((nodeItem) => nodeItem?.node?.breadcrumbBadge)?.node
      ?.breadcrumbBadge;
  }

  private createBadge(
    elemName: string,
    badge: BreadcrumbBadge | undefined,
    containerElement: HTMLElement,
    badgeAlreadyExists: boolean,
  ) {
    const id = `${elemName}-id`;
    if (badgeAlreadyExists) {
      return;
    }

    const communityBadgeRef = createComponent(BreadcrumbBadgeComponent, {
      environmentInjector: this.environmentInjector,
    });

    communityBadgeRef.setInput('badge', badge);
    communityBadgeRef.setInput('id', id);
    communityBadgeRef.setInput('popoverId', `${id}-popover`);

    this.appRef.attachView(communityBadgeRef.hostView);
    const communityBadgeElement = (
      communityBadgeRef.hostView as EmbeddedViewRef<LuigiNode>
    ).rootNodes[0] as HTMLElement;
    communityBadgeElement.classList.add(elemName);
    containerElement.appendChild(communityBadgeElement);
    return;
  }

  private getBreadcrumbLabel(label: string): string {
    if (label === 'Projects') {
      return 'Workspaces';
    }
    return label;
  }

  private getBreadcrumbRoute(item: NodeItem, label: string): string {
    if (label === 'Workspaces') {
      return '/workspaces';
    }
    return item.route ?? '';
  }

  private getConfigBtnData(nodeItems: NodeItem[]): ConfigBtnData {
    const lastNode = nodeItems.at(-1)?.node as LuigiNodeExtended;
    if (!lastNode) {
      return {} as ConfigBtnData;
    }

    const entity = lastNode.installableIn?.toLowerCase();
    const context = lastNode.context;

    const isOwner = !!(
      entity &&
      context?.entityContext?.[entity]?.policies?.includes(
        'extension_administer',
      )
    );

    const { hasConfig, extClass, scope, installableIn } = lastNode;
    return {
      hasConfig,
      extClass,
      scope,
      installableIn,
      isOwner,
      isInstallableInLevel: lastNode?.entityType === entity,
      category: (lastNode?.category as LuigiNodeCategory)?.id ?? '',
    };
  }

  private createConfigBtn(
    containerElement: HTMLElement,
    configBtnData: ConfigBtnData,
  ): void {
    if (this.configBtnComponentComponentRef) {
      this.configBtnComponentComponentRef.setInput(
        'configBtnData',
        configBtnData,
      );

      const nativeElement = this.configBtnComponentComponentRef.location
        .nativeElement as HTMLElement;

      if (!containerElement.querySelector(nativeElement.nodeName)) {
        containerElement.appendChild(nativeElement);
      }
      return;
    }

    this.configBtnComponentComponentRef = createComponent(ConfigBtnComponent, {
      environmentInjector: this.environmentInjector,
    });
    this.configBtnComponentComponentRef.setInput(
      'configBtnData',
      configBtnData,
    );
    this.appRef.attachView(this.configBtnComponentComponentRef.hostView);
    containerElement.appendChild(
      this.configBtnComponentComponentRef.location.nativeElement,
    );
  }

  private getConfigRevokeTrust(
    nodeItems: NodeItem[],
  ): RevokeTrustBtnData | undefined {
    const lastNode = nodeItems.at(-1)?.node as LuigiNodeExtended;
    if (!lastNode) {
      return undefined;
    }

    const context = lastNode.context;
    const projectId = context?.entityContext?.project?.id;
    const extensionLabel = lastNode.label;
    const isProjectLevelExtension = !!lastNode.isProjectLevelExtension;
    return {
      projectId,
      isProjectLevelExtension,
      extensionLabel: extensionLabel ?? '',
    };
  }

  private createRevokeExtensionTrustBtn(
    containerElement: HTMLElement,
    revokeTrustBtnData?: RevokeTrustBtnData,
  ): void {
    if (this.configRevokeTrustComponentRef) {
      this.configRevokeTrustComponentRef.setInput(
        'revokeTrustBtnData',
        revokeTrustBtnData,
      );

      const nativeElement = this.configRevokeTrustComponentRef.location
        .nativeElement as HTMLElement;

      if (!containerElement.querySelector(nativeElement.nodeName)) {
        containerElement.appendChild(nativeElement);
      }
      return;
    }

    this.configRevokeTrustComponentRef = createComponent(
      RevokeExtensionTrustButtonComponent,
      {
        environmentInjector: this.environmentInjector,
      },
    );

    this.appRef.attachView(this.configRevokeTrustComponentRef.hostView);
    containerElement.appendChild(
      this.configRevokeTrustComponentRef.location.nativeElement,
    );
  }

  private getNodeRouteWithQueryParameters(route: string | null): string | null {
    if (!route) {
      return null;
    }

    if (!route.startsWith('/') || route.startsWith('//')) {
      return null;
    }

    const url = new URL(route, window.location.origin);

    if (url.origin !== window.location.origin) {
      return null;
    }

    const currentParams = new URLSearchParams(window.location.search);
    currentParams.forEach((value, key) => {
      url.searchParams.set(key, value);
    });

    return url.href;
  }
}
